import { CircularProgress } from '@mgmresorts/mgm-ui';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { captureException } from '../../../monitoring/logrocket';
import { AuthContext } from '../../../utils/auth/AuthProvider';
import { setIframeError, setIframeToken } from '../../../utils/auth/iframe';
import { actually } from '../../../utils/logging';

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export default function AuthCallbackPage() {
  const { authClient } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);

  const onAuthError = useCallback((error: Error) => {
    setIframeError(error);
    // We change the tag if it is not inside the mobile auth transfer flow
    captureException(
      error,
      window?.localStorage.getItem('mobile-auth-transfer') ? 'mobile-auth-transfer' : 'auth-oidc-error',
    );
  }, []);

  const processSessionDetection = useCallback(async () => {
    if (isInIframe()) {
      // We do not call the sdk method since it tries to get a token, we just need to know if the cookie is set
      const isSessionValid = await authClient.getAuthClient().session.exists();
      if (!isSessionValid) {
        onAuthError(new Error('Session cookie is not present'));
        return;
      }

      setIframeToken({
        expiresAt: 123456,
        value: 'mocked-token-success',
        authorizeUrl: '',
        scopes: [],
        isTransient: false,
        isPerpetual: false,
        loyaltyTier: null,
        accessToken: 'mocked-token-success',
        claims: { sub: '' },
        tokenType: '',
        userinfoUrl: '',
      });

      return;
    }

    try {
      const possibleError = await authClient.acceptTokens({ forceNoRedirect: true });
      if (possibleError) {
        onAuthError(possibleError);
        return;
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        onAuthError(error);
      } else {
        onAuthError(new Error('Unknown error captured while accepting tokens'));
      }
    }

    const token = await authClient.getAccessToken();

    actually.log('TOKEN INSIDE LOGIN REDIRECT', token);

    if (token) setIframeToken(token);
  }, [authClient, onAuthError]);

  useEffect(() => {
    if (authClient && !isLoading) {
      processSessionDetection();
      setIsLoading(true);
    }
  }, [authClient, isLoading, processSessionDetection]);

  return (
    <div data-testid='route-/auth/login'>
      <CircularProgress size={48} indeterminate aria-label='Loading' />
    </div>
  );
}
